// LiteYouTubeEmbed.js — click-to-load YouTube facade (Task #274)
//
// WHY: every live YouTube <iframe> is a full embedded web page (player JS,
// ads/analytics frames, media pipeline). On iPhone Safari, several of these
// stacked on top of the 3D character + streaming audio + open mic is a major
// contributor to iOS WebKit reclaiming (force-reloading) the tab
// mid-conversation. This facade renders only the video thumbnail + a
// YouTube-style play button — visually the same as an unplayed embed — and
// defers ALL player cost until the user actually taps.
//
// Behavior on tap (per user direction, Task #274):
//   - Touch devices (phones/tablets): open the video on YouTube itself
//     (new tab / YouTube app) — no in-page player is ever created.
//   - Desktop: swap the facade for the real iframe with autoplay, so it
//     plays inline exactly where it would have before.
//
// The facade fills its positioned parent (both call sites wrap it in a
// 16:9 padded-box container), so layout is identical to the old iframe.

/**
 * True on touch-first devices (phones/tablets). Desktop stays false even
 * on touch-screen laptops with a fine pointer.
 */
export function isTouchDevice() {
  try {
    if (typeof window === "undefined") return false;
    if (
      typeof window.matchMedia === "function" &&
      window.matchMedia("(pointer: coarse)").matches
    ) {
      return true;
    }
    return (
      "ontouchstart" in window &&
      typeof navigator !== "undefined" &&
      (navigator.maxTouchPoints || 0) > 1
    );
  } catch (_) {
    return false;
  }
}

/**
 * Extract the YouTube video id from the video data object. Prefers the
 * explicit `video_id` field (set by youtube_search_service.py); falls back
 * to parsing the embed iframe src or the watch URL.
 * @returns {string|null}
 */
export function extractYouTubeId(video) {
  if (!video) return null;
  const ID_RE = /^[A-Za-z0-9_-]{6,20}$/;
  if (typeof video.video_id === "string" && ID_RE.test(video.video_id)) {
    return video.video_id;
  }
  const sources = [video.embed_html, video.youtube_url];
  for (const s of sources) {
    if (typeof s !== "string") continue;
    const m =
      s.match(/(?:youtube\.com\/embed\/|youtu\.be\/|[?&]v=)([A-Za-z0-9_-]{6,20})/);
    if (m && ID_RE.test(m[1])) return m[1];
  }
  return null;
}

/**
 * Build a click-to-load facade for a YouTube video.
 *
 * @param {Object} video - video data ({video_id, title, thumbnail,
 *                         youtube_url, embed_html, ...})
 * @param {Object} [opts]
 * @param {string} [opts.borderRadius] - CSS border-radius for the facade.
 * @returns {HTMLElement|null} an element that absolutely fills its
 *          positioned parent, or null when no video id could be resolved
 *          (caller should fall back to its legacy embed path).
 */
export function createLiteYouTubeEmbed(video, opts = {}) {
  const videoId = extractYouTubeId(video);
  if (!videoId) return null;

  const title = (video && video.title) || "YouTube video";
  const watchUrl =
    (video && video.youtube_url) ||
    `https://www.youtube.com/watch?v=${videoId}`;
  const thumbUrl =
    (video && video.thumbnail) ||
    `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
  const borderRadius = opts.borderRadius || "0";

  const facade = document.createElement("div");
  facade.className = "lite-yt-facade";
  facade.setAttribute("role", "button");
  facade.setAttribute("tabindex", "0");
  facade.setAttribute("aria-label", `Play video: ${title}`);
  facade.title = title;
  facade.style.cssText = `
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    cursor: pointer;
    background: #000;
    border-radius: ${borderRadius};
    overflow: hidden;
  `;

  const img = document.createElement("img");
  img.src = thumbUrl;
  img.alt = title;
  img.loading = "lazy";
  img.decoding = "async";
  img.referrerPolicy = "no-referrer";
  img.style.cssText = `
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    border: none;
  `;
  facade.appendChild(img);

  // YouTube-style play button (red rounded rect + white triangle).
  const playBtn = document.createElement("div");
  playBtn.className = "lite-yt-playbtn";
  playBtn.style.cssText = `
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 64px;
    height: 44px;
    pointer-events: none;
  `;
  playBtn.innerHTML = `
    <svg width="100%" height="100%" viewBox="0 0 68 48" aria-hidden="true">
      <path d="M66.52 7.74a8 8 0 0 0-5.63-5.66C55.94.5 34 .5 34 .5s-21.94 0-26.89 1.58a8 8 0 0 0-5.63 5.66C0 12.7 0 24 0 24s0 11.3 1.48 16.26a8 8 0 0 0 5.63 5.66C12.06 47.5 34 47.5 34 47.5s21.94 0 26.89-1.58a8 8 0 0 0 5.63-5.66C68 35.3 68 24 68 24s0-11.3-1.48-16.26z" fill="#f00"></path>
      <path d="M45 24 27 14v20" fill="#fff"></path>
    </svg>
  `;
  facade.appendChild(playBtn);

  const activate = () => {
    if (isTouchDevice()) {
      // Mobile: never create an in-page player — hand off to YouTube
      // (the YouTube app intercepts watch URLs on iOS/Android).
      try {
        window.open(watchUrl, "_blank", "noopener");
      } catch (_) {}
      return;
    }
    // Desktop: swap in the real player, autoplaying, in place.
    const iframe = document.createElement("iframe");
    iframe.src = `https://www.youtube.com/embed/${videoId}?autoplay=1&playsinline=1`;
    iframe.title = title;
    iframe.allow =
      "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share";
    iframe.allowFullscreen = true;
    iframe.referrerPolicy = "strict-origin-when-cross-origin";
    iframe.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      border: none;
      border-radius: ${borderRadius};
    `;
    facade.replaceChildren(iframe);
    facade.style.cursor = "default";
    facade.removeAttribute("role");
    facade.removeAttribute("tabindex");
  };

  facade.addEventListener("click", activate);
  facade.addEventListener("keydown", (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      activate();
    }
  });

  return facade;
}
